import { Country } from '@backmarket/http-api'
import type { ApiSpecs } from '@backmarket/nuxt-module-http-v2/specs'
import { getPhoneNumberInfos } from '@ds/components/InputPhone'

type ShippingAddress =
  ApiSpecs['/bm/cart']['GET']['response']['addressList']['deliver']
type CustomerDetails =
  ApiSpecs['/api/mobile-plan/in-store/v1/magic-links/:magicLinkId']['GET']['response']['customerDetails']

const emptyShippingAddress = {
  firstName: null,
  lastName: null,
  city: null,
  postalCode: null,
  street: null,
  phone: null,
  country: null,
  countryDialInCode: null,
  birthdate: null,
  company: null,
  customerIdNumber: null,
  dateCreation: null,
  dateModification: null,
  email: null,
  firstNamePronunciation: null,
  lastNamePronunciation: null,
  stateOrProvince: null,
  street2: null,
  warnings: [],
}

/**
 * Mapping of ISO 3166-1 alpha-3 country codes to alpha-2 country codes.
 * Based on the international standard from https://www.iban.com/country-codes
 */
const ALPHA3_TO_ALPHA2_COUNTRY_MAP: Record<string, Country> = {
  AFG: Country.AF,
  ALA: Country.AX,
  ALB: Country.AL,
  DZA: Country.DZ,
  ASM: Country.AS,
  AND: Country.AD,
  AGO: Country.AO,
  AIA: Country.AI,
  ATG: Country.AG,
  ARG: Country.AR,
  ARM: Country.AM,
  ABW: Country.AW,
  AUS: Country.AU,
  AUT: Country.AT,
  AZE: Country.AZ,
  BHS: Country.BS,
  BHR: Country.BH,
  BGD: Country.BD,
  BRB: Country.BB,
  BLR: Country.BY,
  BEL: Country.BE,
  BLZ: Country.BZ,
  BEN: Country.BJ,
  BMU: Country.BM,
  BTN: Country.BT,
  BOL: Country.BO,
  BES: Country.BQ,
  BIH: Country.BA,
  BWA: Country.BW,
  BRA: Country.BR,
  IOT: Country.IO,
  BRN: Country.BN,
  BGR: Country.BG,
  BFA: Country.BF,
  BDI: Country.BI,
  CPV: Country.CV,
  KHM: Country.KH,
  CMR: Country.CM,
  CAN: Country.CA,
  CYM: Country.KY,
  CAF: Country.CF,
  TCD: Country.TD,
  CHL: Country.CL,
  CHN: Country.CN,
  CXR: Country.CX,
  CCK: Country.CC,
  COL: Country.CO,
  COM: Country.KM,
  COD: Country.CD,
  COG: Country.CG,
  COK: Country.CK,
  CRI: Country.CR,
  HRV: Country.HR,
  CUB: Country.CU,
  CUW: Country.CW,
  CYP: Country.CY,
  CZE: Country.CZ,
  CIV: Country.CI,
  DNK: Country.DK,
  DJI: Country.DJ,
  DMA: Country.DM,
  DOM: Country.DO,
  ECU: Country.EC,
  EGY: Country.EG,
  SLV: Country.SV,
  GNQ: Country.GQ,
  ERI: Country.ER,
  EST: Country.EE,
  SWZ: Country.SZ,
  ETH: Country.ET,
  FLK: Country.FK,
  FRO: Country.FO,
  FJI: Country.FJ,
  FIN: Country.FI,
  FRA: Country.FR,
  GUF: Country.GF,
  PYF: Country.PF,
  GAB: Country.GA,
  GMB: Country.GM,
  GEO: Country.GE,
  DEU: Country.DE,
  GHA: Country.GH,
  GIB: Country.GI,
  GRC: Country.GR,
  GRL: Country.GL,
  GRD: Country.GD,
  GLP: Country.GP,
  GUM: Country.GU,
  GTM: Country.GT,
  GGY: Country.GG,
  GIN: Country.GN,
  GNB: Country.GW,
  GUY: Country.GY,
  HTI: Country.HT,
  VAT: Country.VA,
  HND: Country.HN,
  HKG: Country.HK,
  HUN: Country.HU,
  ISL: Country.IS,
  IND: Country.IN,
  IDN: Country.ID,
  IRN: Country.IR,
  IRQ: Country.IQ,
  IRL: Country.IE,
  IMN: Country.IM,
  ISR: Country.IL,
  ITA: Country.IT,
  JAM: Country.JM,
  JPN: Country.JP,
  JEY: Country.JE,
  JOR: Country.JO,
  KAZ: Country.KZ,
  KEN: Country.KE,
  KIR: Country.KI,
  PRK: Country.KP,
  KOR: Country.KR,
  KWT: Country.KW,
  KGZ: Country.KG,
  LAO: Country.LA,
  LVA: Country.LV,
  LBN: Country.LB,
  LSO: Country.LS,
  LBR: Country.LR,
  LBY: Country.LY,
  LIE: Country.LI,
  LTU: Country.LT,
  LUX: Country.LU,
  MAC: Country.MO,
  MDG: Country.MG,
  MWI: Country.MW,
  MYS: Country.MY,
  MDV: Country.MV,
  MLI: Country.ML,
  MLT: Country.MT,
  MHL: Country.MH,
  MTQ: Country.MQ,
  MRT: Country.MR,
  MUS: Country.MU,
  MYT: Country.YT,
  MEX: Country.MX,
  FSM: Country.FM,
  MDA: Country.MD,
  MCO: Country.MC,
  MNG: Country.MN,
  MNE: Country.ME,
  MSR: Country.MS,
  MAR: Country.MA,
  MOZ: Country.MZ,
  MMR: Country.MM,
  NAM: Country.NA,
  NRU: Country.NR,
  NPL: Country.NP,
  NLD: Country.NL,
  NCL: Country.NC,
  NZL: Country.NZ,
  NIC: Country.NI,
  NER: Country.NE,
  NGA: Country.NG,
  NIU: Country.NU,
  NFK: Country.NF,
  MNP: Country.MP,
  NOR: Country.NO,
  OMN: Country.OM,
  PAK: Country.PK,
  PLW: Country.PW,
  PSE: Country.PS,
  PAN: Country.PA,
  PNG: Country.PG,
  PRY: Country.PY,
  PER: Country.PE,
  PHL: Country.PH,
  POL: Country.PL,
  PRT: Country.PT,
  PRI: Country.PR,
  QAT: Country.QA,
  MKD: Country.MK,
  ROU: Country.RO,
  RUS: Country.RU,
  RWA: Country.RW,
  REU: Country.RE,
  BLM: Country.BL,
  SHN: Country.SH,
  KNA: Country.KN,
  LCA: Country.LC,
  MAF: Country.MF,
  SPM: Country.PM,
  VCT: Country.VC,
  WSM: Country.WS,
  SMR: Country.SM,
  STP: Country.ST,
  SAU: Country.SA,
  SEN: Country.SN,
  SRB: Country.RS,
  SYC: Country.SC,
  SLE: Country.SL,
  SGP: Country.SG,
  SXM: Country.SX,
  SVK: Country.SK,
  SVN: Country.SI,
  SLB: Country.SB,
  SOM: Country.SO,
  ZAF: Country.ZA,
  SSD: Country.SS,
  ESP: Country.ES,
  LKA: Country.LK,
  SDN: Country.SD,
  SUR: Country.SR,
  SJM: Country.SJ,
  SWE: Country.SE,
  CHE: Country.CH,
  SYR: Country.SY,
  TWN: Country.TW,
  TJK: Country.TJ,
  TZA: Country.TZ,
  THA: Country.TH,
  TLS: Country.TL,
  TGO: Country.TG,
  TKL: Country.TK,
  TON: Country.TO,
  TTO: Country.TT,
  TUN: Country.TN,
  TUR: Country.TR,
  TKM: Country.TM,
  TCA: Country.TC,
  TUV: Country.TV,
  UGA: Country.UG,
  UKR: Country.UA,
  ARE: Country.AE,
  GBR: Country.GB,
  USA: Country.US,
  URY: Country.UY,
  UZB: Country.UZ,
  VUT: Country.VU,
  VEN: Country.VE,
  VNM: Country.VN,
  VGB: Country.VG,
  VIR: Country.VI,
  WLF: Country.WF,
  ESH: Country.EH,
  YEM: Country.YE,
  ZMB: Country.ZM,
  ZWE: Country.ZW,
}

/**
 * Converts ISO 3166-1 alpha-3 country code to alpha-2 country code.
 *
 * @param alpha3Code - Three-letter country code (e.g., 'FRA', 'USA', 'DEU')
 * @returns Two-letter Country enum value, or null if code is not recognized
 *
 * @example
 * ```ts
 * getCountryFromBouyguesCountry('FRA') // Returns Country.FR
 * getCountryFromBouyguesCountry('USA') // Returns Country.US
 * getCountryFromBouyguesCountry('INVALID') // Returns null
 * ```
 */
export function getCountryFromBouyguesCountry(
  alpha3Code: string,
): Country | null {
  return ALPHA3_TO_ALPHA2_COUNTRY_MAP[alpha3Code] ?? null
}

/**
 * Creates or updates a shipping address using customer details from a mobile plan.
 *
 * Takes customer info (name, phone, billing address) and converts it to shipping format.
 * If no existing shipping address is provided, creates a new one with empty defaults.
 *
 * @param shippingAddress - Existing shipping address to update, or null/undefined for new
 * @param customerDetails - Customer info from mobile plan containing name, phone, and billing address
 * @returns Complete shipping address with customer details applied
 *
 * @example
 * ```ts
 * const shipping = getShippingAddressFromCustomerDetails(
 *   existingAddress,
 *   customerFromMobilePlan
 * );
 * ```
 */
export function getAddressFromCustomerDetails(
  shippingAddress: ShippingAddress | null,
  customerDetails: CustomerDetails,
) {
  const addressFromCustomerDetails: Partial<ShippingAddress> = {
    firstName: customerDetails.firstName,
    lastName: customerDetails.lastName,
    city: customerDetails.billingAddress.city,
    postalCode: customerDetails.billingAddress.postCode,
    street: customerDetails.billingAddress.streetNumber
      ? `${customerDetails.billingAddress.streetNumber} ${customerDetails.billingAddress.streetName}`
      : customerDetails.billingAddress.streetName,
    country: getCountryFromBouyguesCountry(
      customerDetails.billingAddress.country,
    ),
  }

  if (customerDetails.phoneNumber) {
    const { dial, nationalNumber } = getPhoneNumberInfos(
      customerDetails.phoneNumber,
    )

    addressFromCustomerDetails.phone = nationalNumber
    addressFromCustomerDetails.countryDialInCode = dial
  }

  if (!shippingAddress) {
    return {
      ...emptyShippingAddress,
      ...addressFromCustomerDetails,
    }
  }

  return {
    ...shippingAddress,
    ...addressFromCustomerDetails,
  }
}
